/**
 * TVG-324: one show round booked on one of a contact's events.
 *
 * The round itself still lives on the event — this is the contact-record view
 * of it. A contact with several events has several of these, each its own
 * booking, potentially at a different venue on a different date.
 */

import { ShowRoundReview } from '@app/show-rounds/show-round-review.model';

/**
 * What became of the round. `booked` is pending: the date is still to come.
 * TVG-379: `rescheduled` is a booked viewing moved to another date.
 */
export type ContactShowRoundOutcome = 'booked' | 'turned_up' | 'cancelled' | 'did_not_show' | 'rescheduled';

/** TVG-379: the outcomes staff can record on a viewing, in the order offered. */
export const CONTACT_SHOW_ROUND_OUTCOMES: { value: ContactShowRoundOutcome; label: string }[] = [
  { value: 'booked', label: 'Booked' },
  { value: 'turned_up', label: 'Turned up' },
  { value: 'did_not_show', label: 'Did not show' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
];

/**
 * TVG-368: the Viewings list's Status column. The same as the outcome, except
 * `completed` once the post-viewing follow-up email has actually sent —
 * `Event::postViewingFollowUpSent()` is the single signal for this.
 */
export type ContactShowRoundStatus = ContactShowRoundOutcome | 'completed';

export interface ContactShowRoundVenue {
  id: number;
  name: string;
}

export interface ContactShowRound {
  /** TVG-379: the viewing's own record; an event can hold several. */
  id: number;
  /** The event the viewing is booked on; the row links through to it. */
  event_id: number;
  event_reference: string | null;
  /** Full ISO timestamp of the booking, used for ordering. */
  booked_at: string;
  date: string;
  /** Null when only a date was captured (the board stores midnight for that). */
  time: string | null;
  venue: ContactShowRoundVenue | null;
  outcome: ContactShowRoundOutcome;
  outcome_label: string;
  status: ContactShowRoundStatus;
  status_label: string;
  /** TVG-379: who showed them round on this viewing, from the admin-managed Show around list. */
  show_around_name_id: number | null;
  show_around_person: string | null;
  /**
   * TVG-339: what the customer answered after this viewing, or null when they
   * have not been asked or have not replied.
   */
  review: ShowRoundReview | null;
}

/** TVG-379: what staff can change on one viewing. */
export interface ContactShowRoundChanges {
  show_around_name_id?: number | null;
  outcome?: ContactShowRoundOutcome;
}

export interface ContactShowRoundsResponse {
  data: ContactShowRound[];
}
